/**
 * Safe `tel:` / `mailto:` hrefs for contact values that come from AppSheet.
 *
 * Contact phone and email cells are free text typed by office staff in another
 * system, so they are UNTRUSTED as far as this app is concerned. A value that
 * already contains a scheme separator (`javascript:`, `data:`, or simply a second
 * `tel:`) must never be interpolated into an href — so any value containing ':'
 * is refused outright and the caller renders plain text instead.
 *
 * A `mailto:` href is more than an address: everything after the address is
 * read by the mail app as header fields ("?bcc=", "&subject=", "?body="), and a
 * comma or semicolon adds recipients. An email cell holding
 * `a@b.com?bcc=someone@else.com` would open the office's mail app with a hidden
 * extra recipient already filled in. So an email becomes a link only when it is
 * ONE plain address; anything else is refused and rendered as plain text, the
 * same way a value carrying a scheme is. Refused, not stripped: a cell that is
 * not a plain address is not something to guess a recipient from.
 *
 * Extracted from `GeneratorContacts` (ISS-463) so the generators list and the
 * generator detail cannot drift on the rule that keeps them safe, and moved here
 * (#6443) because every email link in the client needs the same rule: render one
 * through `components/shared/EmailLink` instead of writing `mailto:${...}`. CI
 * (`scripts/guards/check-no-raw-mailto.mjs`) fails a new raw one.
 *
 * @module lib/contactHref
 */
import { phoneHref } from '@officehub/shared';

/**
 * One plain address: name@domain.tld from an allow-list of characters. The
 * allow-list is what keeps out "?", "&", "#", "%" (so "%3F" and every other
 * encoded form), ",", ";", whitespace and line breaks, without a deny-list that
 * the next encoding trick would walk around.
 */
const PLAIN_EMAIL_ADDRESS = /^[A-Za-z0-9._+'-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)+$/;

/** A `tel:`/`mailto:` href, or undefined when the value must not become a link. */
export function safeContactHref(scheme: 'tel' | 'mailto', value: string | undefined): string | undefined {
  const cleaned = (value ?? '').trim();
  if (!cleaned || cleaned.includes(':')) return undefined;
  if (scheme === 'mailto' && !PLAIN_EMAIL_ADDRESS.test(cleaned)) return undefined;
  return `${scheme}:${cleaned}`;
}

// The phone reader lives in @officehub/shared so the Cloud Functions dial by the same rule
// (L-1156); re-exported here so every client import is unchanged.
export { phoneHref, smsRecipient } from '@officehub/shared';

/**
 * Dial a phone value from a click handler (a menu item, a keyboard shortcut) where there is no
 * `<a>` to put the link on. Same rule as every link: only a number phoneHref can read is dialled.
 * Render the control itself only when `phoneHref('tel', phone)` is non-empty, so it is never dead.
 * @param phone the phone text as stored
 * @return true when a dial was started, false when the value is not a number to dial
 */
export function dialPhone(phone: string | undefined | null): boolean {
  const href = phoneHref('tel', phone);
  if (!href) return false;
  window.location.href = href;
  return true;
}
